import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/lib/utils";
import { Users, AlertCircle, Calculator, CheckCircle2 } from "lucide-react";

const STAFFING_TABLE = [
  { type: "ATR 72 (Turboprop)", pilots: 2, crew: 2, ground: 10 },
  { type: "A320 (Narrow-body)", pilots: 2, crew: 3, ground: 16 },
  { type: "737 MAX (Narrow-body)", pilots: 2, crew: 3, ground: 16 },
  { type: "787 (Wide-body)", pilots: 3, crew: 7, ground: 21 },
];

function StaffingCalculator({ team, updateDecision }) {
  const { t } = useTranslation();
  const [req, setReq] = useState(null);

  useEffect(() => {
    if (!team?.id) return;
    let active = true;
    axios.get(`${API}/teams/${team.id}/staffing-requirements`)
      .then(res => { if (active) setReq(res.data); })
      .catch(() => { if (active) setReq(null); });
    return () => { active = false; };
  }, [team?.id, team?.active_routes, team?.route_config]);

  if (!req) return null;

  const rows = [
    { key: "pilots", label: t("decisions.staffingPilots") },
    { key: "cabin_crew", label: t("decisions.staffingCabin") },
    { key: "ground_staff", label: t("decisions.staffingGround") },
  ];
  const totalGap = req.gap.pilots + req.gap.cabin_crew + req.gap.ground_staff;

  const hireGap = () => {
    updateDecision("pilots_hire", req.gap.pilots);
    updateDecision("cabin_crew_hire", req.gap.cabin_crew);
    updateDecision("ground_staff_hire", req.gap.ground_staff);
  };

  return (
    <Card data-testid="staffing-calculator">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><Calculator className="w-4 h-4" /> {t("decisions.staffingCalculator")}</CardTitle>
        <CardDescription className="text-xs">{t("decisions.staffingCalcDesc", { airports: req.airports_served })}</CardDescription>
      </CardHeader>
      <CardContent>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-slate-500">
              <th className="text-left py-1.5 px-2">{t("decisions.staffingRole")}</th>
              <th className="text-center py-1.5 px-2">{t("decisions.staffingCurrent")}</th>
              <th className="text-center py-1.5 px-2">{t("decisions.staffingRequired")}</th>
              <th className="text-center py-1.5 px-2">{t("decisions.staffingGap")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const gap = req.gap[r.key];
              return (
                <tr key={r.key} className="border-b border-slate-50" data-testid={`staffing-row-${r.key}`}>
                  <td className="py-1.5 px-2">{r.label}</td>
                  <td className="py-1.5 px-2 text-center font-mono">{req.current[r.key]}</td>
                  <td className="py-1.5 px-2 text-center font-mono">{req.required[r.key]}</td>
                  <td className={`py-1.5 px-2 text-center font-mono font-semibold ${gap > 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid={`staffing-gap-${r.key}`}>
                    {gap > 0 ? `+${gap}` : "✓"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {totalGap > 0 ? (
          <Button size="sm" className="mt-3 w-full" onClick={hireGap} data-testid="hire-gap-btn">
            {t("decisions.staffingHireGap")}
          </Button>
        ) : (
          <div className="mt-3 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="staffing-covered">
            <CheckCircle2 className="w-4 h-4 shrink-0" /> {t("decisions.staffingCovered")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function HRSection({ decision, updateDecision, team }) {
  const { t } = useTranslation();
  const currentPilots = team?.pilots || 12;
  const currentCrew = team?.cabin_crew || 12;
  const currentGround = team?.ground_staff || 10;
  const redundancies = decision.staff_redundancies || { pilots: 0, cabin_crew: 0, ground_staff: 0 };

  return (
    <div className="space-y-6">
      <StaffingCalculator team={team} updateDecision={updateDecision} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hiring */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> {t("decisions.staffing")}</CardTitle>
            <CardDescription>{t("decisions.staffingDesc", { pilots: currentPilots, crew: currentCrew, ground: currentGround })}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">{t("decisions.hirePilots")} ($50K)</Label>
                <Input type="number" min={0} max={20} value={decision.pilots_hire || 0} onChange={e => updateDecision("pilots_hire", parseInt(e.target.value) || 0)} className="font-mono h-9 mt-1" data-testid="hire-pilots" />
              </div>
              <div>
                <Label className="text-xs">{t("decisions.hireCabinCrew")} ($15K)</Label>
                <Input type="number" min={0} max={50} value={decision.cabin_crew_hire || 0} onChange={e => updateDecision("cabin_crew_hire", parseInt(e.target.value) || 0)} className="font-mono h-9 mt-1" data-testid="hire-cabin" />
              </div>
              <div>
                <Label className="text-xs">{t("decisions.hireGroundStaff")} ($10K)</Label>
                <Input type="number" min={0} max={30} value={decision.ground_staff_hire || 0} onChange={e => updateDecision("ground_staff_hire", parseInt(e.target.value) || 0)} className="font-mono h-9 mt-1" data-testid="hire-ground" />
              </div>
            </div>

            <div className="border-t pt-4">
              <Label className="text-xs font-semibold text-slate-600">Redundancies (1 quarter severance)</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <Label className="text-[10px]">Fire Pilots</Label>
                  <Input type="number" min={0} max={currentPilots} value={redundancies.pilots} onChange={e => updateDecision("staff_redundancies", { ...redundancies, pilots: parseInt(e.target.value) || 0 })} className="font-mono h-8 text-sm" data-testid="fire-pilots" />
                </div>
                <div>
                  <Label className="text-[10px]">Fire Cabin Crew</Label>
                  <Input type="number" min={0} max={currentCrew} value={redundancies.cabin_crew} onChange={e => updateDecision("staff_redundancies", { ...redundancies, cabin_crew: parseInt(e.target.value) || 0 })} className="font-mono h-8 text-sm" data-testid="fire-cabin" />
                </div>
                <div>
                  <Label className="text-[10px]">Fire Ground</Label>
                  <Input type="number" min={0} max={currentGround} value={redundancies.ground_staff} onChange={e => updateDecision("staff_redundancies", { ...redundancies, ground_staff: parseInt(e.target.value) || 0 })} className="font-mono h-8 text-sm" data-testid="fire-ground" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Compensation & Training */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle>{t("decisions.compensationTraining")}</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-5">
            <div>
              <Label className="text-xs">{t("decisions.salaryLevel")}</Label>
              <div className="space-y-2 mt-2">
                {[
                  { value: "below_20", label: "-20% Below Market", desc: "Saves cost, hurts satisfaction", color: "text-rose-600" },
                  { value: "market", label: "Market Rate", desc: "Standard industry salary", color: "text-slate-600" },
                  { value: "above_10", label: "+10% Above Market", desc: "Improves satisfaction", color: "text-emerald-600" },
                  { value: "above_20", label: "+20% Above Market", desc: "High satisfaction, high cost", color: "text-emerald-700" },
                ].map(opt => (
                  <button key={opt.value} onClick={() => updateDecision("salary_level", opt.value)} className={`w-full p-3 text-left border-2 rounded-lg transition-colors ${decision.salary_level === opt.value ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`salary-${opt.value}`}>
                    <div className="flex justify-between"><span className="font-medium text-sm">{opt.label}</span><span className={`text-xs ${opt.color}`}>{opt.desc}</span></div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-1"><Label className="text-xs">{t("decisions.trainingInvestment")}</Label><span className="font-mono text-sky-600 font-semibold text-xs">{formatCurrency(decision.training_investment || 0)}</span></div>
              <Slider value={[decision.training_investment || 0]} onValueChange={([v]) => updateDecision("training_investment", v)} min={0} max={1000000} step={50000} className="mt-2" data-testid="training-slider" />
              <p className="text-[10px] text-slate-400 mt-1">Improves quality + reliability ratings</p>
            </div>

            <div>
              <Label className="text-xs">{t("decisions.benefitsPackage")}</Label>
              <Select value={decision.benefits_package || "none"} onValueChange={v => updateDecision("benefits_package", v)}>
                <SelectTrigger className="mt-1" data-testid="benefits-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("decisions.benefitsNone")}</SelectItem>
                  <SelectItem value="standard">{t("decisions.benefitsStandard")}</SelectItem>
                  <SelectItem value="premium">{t("decisions.benefitsPremium")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staffing Requirements Reference */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Staffing Requirements per Aircraft Type</CardTitle><CardDescription className="text-xs">Pilots & cabin crew are per aircraft in service. Ground staff is required at <strong>each airport served</strong> (hub + every destination), per distinct aircraft type operating there.</CardDescription></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[350px]">
            <thead><tr className="border-b"><th className="text-left py-1.5 px-2">Type</th><th className="text-center py-1.5 px-2">Pilots</th><th className="text-center py-1.5 px-2">Cabin Crew/Flight</th><th className="text-center py-1.5 px-2">Ground Staff/Airport</th></tr></thead>
            <tbody>
              {STAFFING_TABLE.map((row, i) => (
                <tr key={i} className="border-b border-slate-50"><td className="py-1.5 px-2">{row.type}</td><td className="py-1.5 px-2 text-center font-mono">{row.pilots}</td><td className="py-1.5 px-2 text-center font-mono">{row.crew}</td><td className="py-1.5 px-2 text-center font-mono">{row.ground}</td></tr>
              ))}
            </tbody>
          </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
